import React from 'react';
import { useLang } from '../../contexts/LangContext.js';
import { useTheme } from '../../contexts/ThemeContext.js';
import { ModalSectionTitle } from '../shared/helpers.jsx';

// ============================================================
// EnvModal.jsx — 주행 환경 설정 모달 (간소화)
// 주행 사이클 선택 → 헤더 🛣️ 드롭박스
// Full-Spec CSV → ⚙️ 설정 > 프리셋 관리
// ============================================================

function EnvModal({ show, onClose, paramsA, paramsB, setParamsA, setParamsB, onApply }) {
  const COLORS = useTheme();
  const lang = useLang();
  const e = (ko, en) => lang === "en" ? en : ko;
  const mono = "'JetBrains Mono', 'Fira Mono', monospace";

  if (!show) return null;

  const apply = (k, v) => {
    setParamsA(p => ({ ...p, [k]: v }));
    setParamsB(p => ({ ...p, [k]: v }));
  };
  const ref = paramsA;

  const CYCLE_LABELS = {
    WLTP: "🌍 WLTP", FTP75: "🇺🇸 FTP-75", EPA_HWY: "🇺🇸 EPA Hwy",
    EPA_CITY: "🇺🇸 EPA City", JC08: "🇯🇵 JC08", CLTC: "🇨🇳 CLTC",
    RDE_FULL: "🛣️ RDE Full", RDE_URBAN: "🏙️ RDE Urban",
    RDE_RURAL: "🌿 RDE Rural", RDE_MOTORWAY: "🚀 RDE Motorway",
    csv_full: "📂 Full-Spec CSV",
  };
  const activeCsvMode = ref.driving_cycle_mode || "default";
  const activeCycleId = activeCsvMode === "csv_full" ? "csv_full" : (ref.cycle_type || "WLTP");
  const activeCycleLabel = CYCLE_LABELS[activeCycleId] || activeCycleId;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 2000,
      display: "flex", alignItems: "center", justifyContent: "center"
    }} onClick={(ev) => { if (ev.target === ev.currentTarget) { onApply && onApply(); onClose(); } }}>
      <div style={{
        background: COLORS.panel, border: `1px solid ${COLORS.border}`, color: COLORS.text,
        borderRadius: 12, width: "min(520px, 96vw)", maxHeight: "85vh",
        display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.5)"
      }} onClick={(ev) => ev.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${COLORS.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: `linear-gradient(135deg, ${COLORS.panel}, ${COLORS.bg})`, borderRadius: "12px 12px 0 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22 }}>🌍</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{e("주행 환경 설정", "Driving Environment")}</div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: mono, marginTop: 2 }}>
                {e("현재 사이클","Cycle")}: <span style={{ color: "#3b82f6", fontWeight: 700 }}>{activeCycleLabel}</span>
                {activeCsvMode === "csv_full" && ref.driving_cycle_name && (
                  <span style={{ marginLeft: 6, color: "#ec4899" }}>· {ref.driving_cycle_name}</span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: COLORS.textDim, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>✕</button>
        </div>

        {/* Content */}
        <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1 }}>

          {/* Full-spec 토글 */}
          {activeCsvMode === "default" && (
            <div style={{ marginBottom: 16 }}>
              <ModalSectionTitle icon="🔬" title={e("Full-spec 사이클 분석", "Full-spec Cycle Analysis")} color="#8b5cf6" />
              <div style={{ padding: "10px 14px", background: COLORS.bg, borderRadius: 8,
                border: `1px solid ${ref.cycle_fullspec ? "#8b5cf6" : COLORS.border}`,
                display: "flex", alignItems: "center", gap: 12, transition: "border-color 0.2s" }}>
                <button onClick={() => apply("cycle_fullspec", !ref.cycle_fullspec)}
                  style={{ padding: "4px 12px", borderRadius: 5, cursor: "pointer", fontWeight: 700, fontSize: 11,
                    background: ref.cycle_fullspec ? "#8b5cf6" : "transparent",
                    color: ref.cycle_fullspec ? "#fff" : COLORS.textDim,
                    border: `1px solid ${ref.cycle_fullspec ? "#8b5cf6" : COLORS.border}`,
                    transition: "all 0.15s" }}>
                  {ref.cycle_fullspec ? "ON" : "OFF"}
                </button>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#8b5cf6" }}>
                    {e("다중 온도·부하·드라이빙 모드 종합 분석", "Multi-temp · multi-load · drive-mode full analysis")}
                  </div>
                  <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 2 }}>
                    {e("그래프에 심화 지표 표시됨", "Enhanced graph labels enabled")}
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeCsvMode === "csv_full" && (
            <div style={{ marginBottom: 16, padding: "10px 14px", background: "#ec489910", borderRadius: 8,
              border: "1px solid #ec489940" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#ec4899", marginBottom: 4 }}>
                📂 Full-Spec CSV {e("모드 활성","Mode Active")}
              </div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: mono }}>
                ⚠️ {e("Full-spec 옵션은 기본 시뮬레이션 모드에서만 사용 가능합니다.", "Full-spec option is only available in Default Simulation mode.")}
              </div>
            </div>
          )}

          {/* 안내 */}
          <div style={{ padding: "14px 16px", background: `${COLORS.accent}10`, borderRadius: 8,
            border: `1px dashed ${COLORS.accent}40`, fontSize: 10, color: COLORS.textMuted, lineHeight: 1.9 }}>
            <div style={{ fontWeight: 700, color: COLORS.text, marginBottom: 6, fontSize: 11 }}>
              💡 {e("주행 사이클 선택 위치 변경 안내", "Driving Cycle Selector Moved")}
            </div>
            <div>
              {e(
                "주행 사이클 선택 (WLTP · FTP-75 · JC08 등 11종)은 헤더 인라인 패널의 🛣️ 드롭박스에서 변경 가능합니다.",
                "Driving cycle selection (11 types) is now in the 🛣️ dropdown in the header inline panel."
              )}
            </div>
            <div style={{ marginTop: 4 }}>
              {e(
                "Full-Spec CSV 업로드 및 템플릿 다운로드는 ⚙️ 설정 → 프리셋 관리 하단에 있습니다.",
                "Full-Spec CSV upload and template download are under ⚙️ Settings → Preset Manager."
              )}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px", borderTop: `1px solid ${COLORS.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: mono }}>
            {e("⚡ 적용 즉시 시뮬레이션 자동 재실행","⚡ Auto re-runs simulation on apply")}
          </div>
          <button onClick={() => { onApply && onApply(); onClose(); }}
            style={{ background: `linear-gradient(135deg, ${COLORS.accent}, #d97706)`,
              border: "none", color: "#000", padding: "8px 24px", borderRadius: 6,
              cursor: "pointer", fontWeight: 700, fontSize: 12 }}>
            {e("✓ 적용 & 재실행","✓ Apply & Re-run")}
          </button>
        </div>

      </div>
    </div>
  );
}

export {
  EnvModal,
};